import React, { useMemo } from "react";
import { formatPeso } from "../GroupedInventoryTable/formatPeso";

/**
 * GroupedInventarioTable — Renders the full inventario report data
 * grouped by product (rowSpan on identity columns).
 *
 * Unlike GroupedInventoryTable (which transforms porBodega structure),
 * this component works with flat rows (one per product-bodega) and groups them
 * by idProducto for visual presentation.
 *
 * Props:
 * - data: Array<InventarioRow> — Flat rows from API (one per product-bodega)
 * - columns: Array<{ key, label, render?, sortable? }> — Column definitions
 * - loading: boolean
 * - emptyMessage: string
 * - identityKeys: string[] — Keys that should be grouped (rowSpan) per product
 * - groupByKey: string — Field to group rows by (default: "idProducto")
 * - renderCell: (row, column) => ReactNode — Optional custom cell renderer
 */
export function GroupedInventarioTable({
  data,
  columns,
  loading,
  emptyMessage = "No hay datos de inventario disponibles.",
  identityKeys = ["idProducto", "codigoProveedor", "marca", "nombreProducto", "pesoKg"],
  groupByKey = "idProducto",
  renderCell,
}) {
  // Group rows by product
  const groups = useMemo(() => {
    if (!data || data.length === 0) return [];

    const grouped = [];
    let currentGroup = null;

    for (const row of data) {
      const key = row[groupByKey];
      if (!currentGroup || currentGroup.key !== key) {
        currentGroup = { key, rows: [row] };
        grouped.push(currentGroup);
      } else {
        currentGroup.rows.push(row);
      }
    }

    return grouped;
  }, [data, groupByKey]);

  const colCount = columns.length;

  if (loading) {
    return (
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <TableHeader columns={columns} />
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={`skeleton-${i}`} className="animate-pulse">
                {Array.from({ length: colCount }).map((_, j) => (
                  <td key={j} className="px-3 py-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <TableHeader columns={columns} />
          <tbody className="bg-white">
            <tr>
              <td
                colSpan={colCount}
                className="px-4 py-8 text-center text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <TableHeader columns={columns} />
        <tbody>
          {groups.map((group, groupIndex) => {
            const bgClass = groupIndex % 2 === 0 ? "bg-white" : "bg-gray-50";
            const borderClass = groupIndex > 0 ? "border-t-2 border-gray-300" : "";

            return group.rows.map((row, rowIndex) => {
              const isFirstRow = rowIndex === 0;

              return (
                <tr
                  key={`${group.key}-${rowIndex}`}
                  className={`${bgClass}${isFirstRow ? ` ${borderClass}` : ""}`}
                >
                  {columns.map((col) => {
                    const isIdentity = identityKeys.includes(col.key);

                    // Identity columns only render on first row with rowSpan
                    if (isIdentity && !isFirstRow) return null;

                    const cellProps = isIdentity
                      ? {
                          rowSpan: group.rows.length,
                          className: "px-3 py-2 text-sm text-gray-700 align-middle",
                        }
                      : {
                          className: "px-3 py-1 text-sm text-gray-700",
                        };

                    let content;
                    if (renderCell) {
                      content = renderCell(row, col);
                    } else if (col.render) {
                      content = col.render(row);
                    } else {
                      const value = row[col.key];
                      if (col.key === "pesoKg") {
                        content = formatPeso(value);
                      } else if (typeof value === "number" && value === 0 && !isIdentity) {
                        return (
                          <td key={col.key} {...cellProps} className="px-3 py-1 text-sm text-gray-400">
                            {value}
                          </td>
                        );
                      } else {
                        content = value ?? "–";
                      }
                    }

                    return (
                      <td key={col.key} {...cellProps}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            });
          })}
        </tbody>
      </table>
    </div>
  );
}

function TableHeader({ columns }) {
  return (
    <thead className="bg-gray-50">
      <tr>
        {columns.map((col) => (
          <th
            key={col.key}
            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            {col.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

export default GroupedInventarioTable;
